import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { addDays, format } from "date-fns";
import moment from "moment";
import { useState } from "react";
import { DateRangePicker, Range } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import useLang from "../../hooks/useLanguage";
import { DateTimePickerStyling } from "../../../Utils/Common/DateTimePickerStyling";
import { CrossIcon } from "../../Icons";
import "./DatePicker.css";

const RANGE_VALUE = /^(\d{2}\/\d{2}\/\d{4}) to (\d{2}\/\d{2}\/\d{4})$/;

const toRange = (value?: string): Range => {
  const match = String(value ?? "")
    .trim()
    .match(RANGE_VALUE);
  const start = match ? moment(match[1], "MM/DD/YYYY", true) : null;
  const end = match ? moment(match[2], "MM/DD/YYYY", true) : null;

  return start?.isValid() && end?.isValid()
    ? { startDate: start.toDate(), endDate: end.toDate(), key: "selection" }
    : { startDate: new Date(), endDate: new Date(), key: "selection" };
};

/**
 * Date range filter for pages that keep their filters in plain state. It has the
 * same API as SearchDatePicker (`onChange({ target: { name, value } })`) but the
 * value is "MM/DD/YYYY to MM/DD/YYYY", the same text the View Requisition range
 * picker produces. A single click selects one day (start and end are the same).
 */
export default function SearchDateRangePicker({
  value,
  onChange,
  name,
  id,
}: any) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  const emit = (newValue: string) => onChange({ target: { name, value: newValue } });

  const orientation = window.matchMedia("(max-width: 700px)").matches
    ? "vertical"
    : "horizontal";

  return (
    <div>
      <input
        id={id ?? name}
        name={name}
        readOnly
        title={value || ""}
        value={value || ""}
        placeholder="MM/DD/YYYY"
        className="form-control bg-white mb-lg-0 h-30px rounded-2 fs-8"
        onClick={() => setOpen(true)}
      />
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={DateTimePickerStyling as any}>
          <Box onClick={() => setOpen(false)} className="icon-container">
            <CrossIcon />
          </Box>
          <DateRangePicker
            // `orientation` is supported by the library but missing from its typings
            {...({ orientation } as object)}
            onChange={(item) => {
              const { startDate, endDate } = item.selection;
              if (!startDate || !endDate) return;
              emit(
                `${format(startDate, "MM/dd/yyyy")} to ${format(
                  endDate,
                  "MM/dd/yyyy"
                )}`
              );
            }}
            editableDateInputs={true}
            moveRangeOnFirstSelection={false}
            ranges={[toRange(value)]}
            months={2}
            direction="horizontal"
            maxDate={addDays(new Date(), 0)}
          />
          <div
            style={{ textAlign: "end", padding: "0% 2%" }}
            className="d-flex justify-content-end gap-2"
          >
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                emit("");
                setOpen(false);
              }}
            >
              {t("Clear")}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setOpen(false)}
            >
              {t("Submit")}
            </button>
          </div>
        </Box>
      </Modal>
    </div>
  );
}
